import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Composer } from './Chat.jsx';
import { Ic } from './Icons.jsx';
import '../styles/voice.css';

// Presentation only: LiveMode continues to own the connection and media resources.
export default function VoiceWorkspace({ locale, modelName, elapsed, status, statusText, muted, visualRef,
  muteDisabled, onMute, onEnd, messages, renderMessage, draft, onDraft, onSend, canSend,
  error, warning, course, onFinish, agentPanel, settingsPanel }) {
  const tr = (zh, en) => locale.startsWith('zh') ? zh : en;
  const [view, setView] = useState('orb');
  const [panel, setPanel] = useState(null);
  const [away, setAway] = useState(false);
  const surface = useRef(null), orb = useRef(null), transcript = useRef(null), content = useRef(null);
  const panelOpener = useRef(null);
  const following = useRef(true), anchor = useRef(0), restoring = useRef(false);
  const visible = view === 'transcript';
  const jump = () => {
    following.current = true; setAway(false);
    if (transcript.current) transcript.current.scrollTop = transcript.current.scrollHeight;
  };
  useLayoutEffect(() => {
    const node = transcript.current;
    if (!visible || !node) return;
    restoring.current = true;
    node.scrollTop = following.current ? node.scrollHeight : anchor.current;
    const frame = requestAnimationFrame(() => { restoring.current = false; });
    return () => cancelAnimationFrame(frame);
  }, [visible, messages]);
  useEffect(() => {
    const node = surface.current;
    const viewport = window.visualViewport;
    const resize = () => {
      if (!node || !viewport) return;
      node.style.setProperty('--voice-height', `${viewport.height}px`);
      node.style.setProperty('--voice-top', `${viewport.offsetTop}px`);
      node.dataset.compact = String(viewport.height < 600);
    };
    resize(); viewport?.addEventListener('resize', resize); viewport?.addEventListener('scroll', resize);
    return () => { viewport?.removeEventListener('resize', resize); viewport?.removeEventListener('scroll', resize); };
  }, []);
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (visible && following.current && transcript.current) transcript.current.scrollTop = transcript.current.scrollHeight;
    });
    if (transcript.current) observer.observe(transcript.current);
    if (content.current) observer.observe(content.current);
    return () => observer.disconnect();
  }, [visible]);
  const toggle = () => {
    if (visible) {
      anchor.current = transcript.current?.scrollTop || 0;
      surface.current?.querySelector('textarea')?.blur();
    }
    setView(visible ? 'orb' : 'transcript');
    orb.current?.focus();
  };
  const switchLabel = visible ? tr('返回语音圆球', 'Show voice orb') : tr('显示文字对话', 'Show conversation');
  const panelTitle = panel === 'settings' ? tr('语音设置', 'Voice settings') : tr('咨询详情与历史', 'Consultation details and history');
  const openPanel = (name, event) => { panelOpener.current = event.currentTarget; setPanel(name); };
  return <Dialog.Root open onOpenChange={open => { if (!open) { if (panel) setPanel(null); else onEnd(); } }}>
    <Dialog.Portal container={document.querySelector('.original-demo') || undefined}>
      <Dialog.Content ref={surface} className="voice-workspace" data-view={view}
        onOpenAutoFocus={event => { event.preventDefault(); orb.current?.focus(); }}
        onEscapeKeyDown={event => { if (panel) { event.preventDefault(); setPanel(null); } }}
        onPointerDownOutside={event => event.preventDefault()}>
        <header className="voice-header">
          <div className="voice-heading"><Dialog.Title>{modelName || 'Lyncia'}</Dialog.Title>
            <Dialog.Description className="voice-state" role="status">{statusText}</Dialog.Description></div>
          <span className="voice-time">{elapsed}</span>
          {settingsPanel && <button className="icon-btn" aria-label={tr('语音设置', 'Voice settings')} onClick={event => openPanel('settings', event)}><Ic.Sliders size={20} /></button>}
          {agentPanel && <button className="voice-details-button" onClick={event => openPanel('course', event)}>{tr('咨询详情', 'Details')}</button>}
        </header>
        <div className="voice-notices">
          {error && <p className="ap-error" role="alert">{error}</p>}
          {warning && <p className="ap-banner" role="status">{warning}</p>}
        </div>
        <section id="voice-transcript" className="voice-transcript live-chat" hidden={!visible} aria-label={tr('对话记录', 'Conversation')}>
          <div className="live-chat-body" ref={transcript} tabIndex={0} onScroll={() => {
            if (!visible || restoring.current) return;
            const node = transcript.current;
            anchor.current = node.scrollTop;
            following.current = node.scrollHeight - node.clientHeight - node.scrollTop <= 80;
            setAway(!following.current);
          }}>
            <div ref={content} className="voice-messages">{messages.length ? messages.map(renderMessage)
              : <p className="live-chat-empty">{tr('可以开口，也可以打字，按你的节奏来。', 'Speak or type whenever you’re ready. Take your time.')}</p>}</div>
          </div>
          {away && <button className="voice-jump" onClick={jump}>{tr('回到最新', 'Jump to latest')}</button>}
        </section>
        <div className="voice-orb-area">
          <button ref={orb} className="voice-orb-toggle" aria-label={switchLabel} aria-expanded={visible}
            aria-controls="voice-transcript" onClick={toggle}>
            <span ref={visualRef} className="voice-orb" data-active={!muted && status === 'listening' || status === 'speaking'} data-state={status} aria-hidden="true" />
          </button>
          <span className="voice-switch-hint" aria-hidden="true">{switchLabel}</span>
        </div>
        <footer className="voice-footer">
          <div className="live-text-composer"><Composer value={draft} onChange={onDraft} locale={locale}
            ariaLabel={tr('在语音对话中输入文字', 'Type in the voice conversation')} maxLength={12000}
            onFocus={() => setView('transcript')} onSend={() => { jump(); onSend(); }} sendDisabled={!canSend} /></div>
          <div className="live-controls voice-controls">
            <button className={'lc-btn' + (muted ? ' muted' : '')} title="Mute" aria-label={muted ? tr('取消静音', 'Unmute') : tr('静音', 'Mute')}
              aria-pressed={muted} disabled={muteDisabled} onClick={onMute}>{muted ? <Ic.MicOff size={22} /> : <Ic.Mic size={22} />}</button>
            {course.start && !course.finish && <button className="voice-archive" onClick={onFinish}
              disabled={course.archiving || course.archiveDisconnected || (course.archivePending && !course.archiveRetry)}>
              {course.archiving ? tr('正在归档…', 'Archiving…') : course.archiveRetry ? tr('重试归档', 'Retry archive') : tr('结束并归档', 'Finish and archive')}</button>}
            <button className="lc-btn end voice-exit" title={tr('退出', 'Exit')} aria-label={tr('退出语音', 'Leave voice')}
              disabled={course.archivePending && !course.archiveQueued} onClick={onEnd}><Ic.Close size={22} /><span>{tr('退出', 'Exit')}</span></button>
          </div>
        </footer>
        <Dialog.Root open={!!panel} onOpenChange={open => { if (!open) setPanel(null); }}>
          <Dialog.Portal container={surface.current || undefined}>
            <Dialog.Overlay className="voice-panel-overlay" />
            <Dialog.Content className="voice-panel" aria-describedby={undefined}
              onEscapeKeyDown={event => { event.preventDefault(); event.stopPropagation(); setPanel(null); }}
              onCloseAutoFocus={event => { event.preventDefault(); panelOpener.current?.focus(); }}>
              <header><Dialog.Title>{panelTitle}</Dialog.Title><Dialog.Close className="icon-btn" aria-label={tr('关闭面板', 'Close panel')}><Ic.Close /></Dialog.Close></header>
              <div className="voice-panel-body">{panel === 'settings' ? settingsPanel : agentPanel}</div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
